/**
 * gestureTemporal.js — motion-based gesture scorers for SignVoice Phase 4.
 *
 * Operates on a circular landmarkBuffer of `[{lm, ts}, …]` shaped frames.
 * Each scorer returns a confidence in [0, 1]. The classifier picks the best
 * scorer above CONFIDENCE_THRESHOLD.
 *
 * 4 motion patterns (Phase 4 Level-1):
 *   forward_from_chin → thanks      (flat hand near chin, moves down/forward)
 *   circular_chest    → please      (open hand circling on chest area)
 *   pinch_to_mouth    → i_want_to_eat (pinched fingers rising toward mouth)
 *   lateral_wave      → goodbye     (open hand oscillating side to side)
 *
 * Each `MOTION_THRESHOLDS.*` is documented inline so users can tune them.
 */

// ============================================================
// Geometric helpers (local — kept independent of gestureHeuristics)
// ============================================================
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const palmCenter = (lm) => ({
  x: (lm[0].x + lm[5].x + lm[9].x + lm[13].x + lm[17].x) / 5,
  y: (lm[0].y + lm[5].y + lm[9].y + lm[13].y + lm[17].y) / 5,
});
const palmLength = (lm) => dist(lm[0], lm[9]);

function isOpenHand(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return false;
  const palm = palmCenter(lm);
  const tips = [4, 8, 12, 16, 20];
  const mcps = [2, 5, 9, 13, 17];
  let ext = 0;
  for (let i = 0; i < 5; i++) {
    if (dist(lm[tips[i]], palm) > dist(lm[mcps[i]], palm) * 1.3) ext++;
  }
  return ext >= 4;
}

function isFlatHand(lm) {
  // 4 fingers extended upward (thumb optional)
  let ext = 0;
  const tips = [8, 12, 16, 20];
  const mcps = [5, 9, 13, 17];
  for (let i = 0; i < 4; i++) {
    if (lm[mcps[i]].y - lm[tips[i]].y > 0.02) ext++;
  }
  return ext >= 3;
}

// ============================================================
// MOTION_THRESHOLDS — tune here
// ============================================================
// THRESHOLD: TEMPORAL_CONFIDENCE — minimum confidence a temporal scorer must
//   return for the classifier to consider it. Lower (0.65) = more permissive
//   but more false positives; higher (0.85) = strict.
const TEMPORAL_CONFIDENCE = 0.75;

// THRESHOLD: MIN_BUFFER_FRAMES — minimum frames a scorer requires to evaluate.
//   At ~30fps, 15 ≈ 500ms of motion. Going below 12 yields jittery detections.
const MIN_BUFFER_FRAMES = 15;

// THRESHOLD: MOTION_SUPPRESS_THRESHOLD — total wrist displacement (sum of frame
//   deltas) over the last 10 frames above which static gestures are SUPPRESSED.
//   Prevents Hello/Yes/Help from misfiring during a goodbye wave or thanks
//   motion. 0.10 ≈ 10 % of frame width — adjust if static signs misfire mid-motion.
const MOTION_SUPPRESS_THRESHOLD = 0.10;

// THRESHOLD: MOTION_DETECT_THRESHOLD — same metric, but for the "motion-detecting"
//   HAND_STATUS UI chip. Higher than suppress so a hand resting in frame doesn't
//   constantly flash the chip. 0.15 ≈ noticeable but intentional motion.
const MOTION_DETECT_THRESHOLD = 0.15;

// THRESHOLD: THANKS_DOWNWARD_DELTA — wrist Y must drop by at least this much
//   between the first and last third of the buffer for a Thanks motion. Camera
//   Y axis: 0 = top of frame, 1 = bottom. So "down" means y increased.
const THANKS_DOWNWARD_DELTA = 0.05;

// THRESHOLD: THANKS_START_Y_MAX — palm centroid y in the first third of the
//   buffer must be ABOVE this (≈ upper half of the frame, proxy for chin/face area).
const THANKS_START_Y_MAX = 0.55;

// THRESHOLD: PLEASE_MIN_SWEEP_RADIANS — minimum cumulative angular sweep
//   (sum of |Δangle|) the wrist must trace around its centroid for Please.
//   π = half circle, 2π = full circle. 1.4·π ≈ 250° works for a small circular gesture.
const PLEASE_MIN_SWEEP_RADIANS = Math.PI;

// THRESHOLD: PLEASE_MIN_RADIUS — minimum mean distance from wrist to its
//   centroid path across the buffer. Filters out tiny jitter circles.
const PLEASE_MIN_RADIUS = 0.02;

// THRESHOLD: PLEASE_CHEST_Y_RANGE — palm centroid y must fall in [min, max]
//   for the gesture to be on the chest area (avoids floating face/lap circles).
const PLEASE_CHEST_Y_MIN = 0.30;
const PLEASE_CHEST_Y_MAX = 0.85;

// THRESHOLD: EAT_PINCH_RATIO — tip4–tip8 distance must be < this × palmLen
//   to count as a pinch in I-want-to-eat.
const EAT_PINCH_RATIO = 0.45;

// THRESHOLD: EAT_UPWARD_DELTA — wrist Y must DECREASE by at least this much
//   between first and last third of buffer (hand rising toward mouth).
const EAT_UPWARD_DELTA = 0.08;

// THRESHOLD: GOODBYE_MIN_CROSSINGS — number of times the wrist X must cross
//   its mean. 2 = a full oscillation (right→left→right or vice versa).
const GOODBYE_MIN_CROSSINGS = 2;

// THRESHOLD: GOODBYE_MIN_AMPLITUDE — max-min of wrist X across the buffer.
//   0.08 ≈ 8 % of frame width — a visible wave, not a twitch.
const GOODBYE_MIN_AMPLITUDE = 0.08;

// ============================================================
// Public motion helpers
// ============================================================

/** Total wrist displacement summed across the last `window` buffer frames. */
export function bufferMotion(buffer, window = 10) {
  if (!Array.isArray(buffer) || buffer.length < 2) return 0;
  const slice = buffer.slice(-Math.min(window, buffer.length));
  let total = 0;
  for (let i = 1; i < slice.length; i++) {
    const a = slice[i - 1].lm[0];
    const b = slice[i].lm[0];
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

export function isInMotion(buffer) {
  return bufferMotion(buffer, 10) > MOTION_SUPPRESS_THRESHOLD;
}

export function hasNoticeableMotion(buffer) {
  return bufferMotion(buffer, 15) > MOTION_DETECT_THRESHOLD;
}

// ============================================================
// Scorers
// ============================================================

/** thanks — flat hand near upper frame moving downward/forward. */
export function scoreForwardFromChin(buffer) {
  if (buffer.length < MIN_BUFFER_FRAMES) return 0;
  const third = Math.floor(buffer.length / 3);
  const first = buffer.slice(0, third);
  const last = buffer.slice(-third);

  const startFlat = first.filter((f) => isFlatHand(f.lm)).length / first.length;
  const endFlat = last.filter((f) => isFlatHand(f.lm)).length / last.length;
  if (startFlat < 0.5 || endFlat < 0.4) return 0;

  const startY = first.reduce((s, f) => s + palmCenter(f.lm).y, 0) / first.length;
  const endY = last.reduce((s, f) => s + palmCenter(f.lm).y, 0) / last.length;
  if (startY > THANKS_START_Y_MAX) return 0;

  const downwardDelta = endY - startY;
  if (downwardDelta < THANKS_DOWNWARD_DELTA) return 0;

  const deltaScore = Math.min(downwardDelta * 5, 1);
  return Math.min(1, startFlat * 0.35 + endFlat * 0.25 + deltaScore * 0.40);
}

/** please — open hand circling in the chest region (≥ π radians of sweep). */
export function scoreCircularChest(buffer) {
  if (buffer.length < 18) return 0;

  const openRatio = buffer.filter((f) => isOpenHand(f.lm)).length / buffer.length;
  if (openRatio < 0.6) return 0;

  const palms = buffer.map((f) => palmCenter(f.lm));
  const cx = palms.reduce((s, p) => s + p.x, 0) / palms.length;
  const cy = palms.reduce((s, p) => s + p.y, 0) / palms.length;
  if (cy < PLEASE_CHEST_Y_MIN || cy > PLEASE_CHEST_Y_MAX) return 0;

  // Cumulative angular sweep relative to centroid
  let totalSweep = 0;
  let prevAngle = Math.atan2(palms[0].y - cy, palms[0].x - cx);
  for (let i = 1; i < palms.length; i++) {
    const angle = Math.atan2(palms[i].y - cy, palms[i].x - cx);
    let delta = angle - prevAngle;
    if (delta > Math.PI) delta -= 2 * Math.PI;
    if (delta < -Math.PI) delta += 2 * Math.PI;
    totalSweep += Math.abs(delta);
    prevAngle = angle;
  }
  if (totalSweep < PLEASE_MIN_SWEEP_RADIANS) return 0;

  // Filter out tiny jitter circles
  const meanR = palms.reduce((s, p) => s + Math.hypot(p.x - cx, p.y - cy), 0) / palms.length;
  if (meanR < PLEASE_MIN_RADIUS) return 0;

  const sweepScore = Math.min(totalSweep / (Math.PI * 2), 1);
  return Math.min(1, openRatio * 0.30 + sweepScore * 0.55 + Math.min(meanR * 12, 1) * 0.15);
}

/** i_want_to_eat — pinch handshape rising toward mouth (wrist y decreases). */
export function scorePinchToMouth(buffer) {
  if (buffer.length < MIN_BUFFER_FRAMES) return 0;

  const pinchRatio = buffer.filter((f) => {
    const palmLen = palmLength(f.lm);
    if (palmLen < 0.001) return false;
    return dist(f.lm[4], f.lm[8]) < palmLen * EAT_PINCH_RATIO;
  }).length / buffer.length;
  if (pinchRatio < 0.5) return 0;

  const third = Math.floor(buffer.length / 3);
  const first = buffer.slice(0, third);
  const last = buffer.slice(-third);
  const startY = first.reduce((s, f) => s + f.lm[0].y, 0) / first.length;
  const endY = last.reduce((s, f) => s + f.lm[0].y, 0) / last.length;
  const upwardDelta = startY - endY;
  if (upwardDelta < EAT_UPWARD_DELTA) return 0;

  const deltaScore = Math.min(upwardDelta * 4, 1);
  return Math.min(1, pinchRatio * 0.55 + deltaScore * 0.45);
}

/** goodbye — open hand oscillating laterally (≥ 2 X-axis crossings). */
export function scoreLateralWave(buffer) {
  if (buffer.length < MIN_BUFFER_FRAMES) return 0;

  const openRatio = buffer.filter((f) => isOpenHand(f.lm)).length / buffer.length;
  if (openRatio < 0.6) return 0;

  const xs = buffer.map((f) => f.lm[0].x);
  const meanX = xs.reduce((a, b) => a + b, 0) / xs.length;
  let crossings = 0;
  let prevSide = xs[0] >= meanX ? 1 : -1;
  for (let i = 1; i < xs.length; i++) {
    const side = xs[i] >= meanX ? 1 : -1;
    if (side !== prevSide) {
      crossings++;
      prevSide = side;
    }
  }
  if (crossings < GOODBYE_MIN_CROSSINGS) return 0;

  const range = Math.max(...xs) - Math.min(...xs);
  if (range < GOODBYE_MIN_AMPLITUDE) return 0;

  const crossScore = Math.min(crossings / 4, 1);
  const rangeScore = Math.min(range * 5, 1);
  return Math.min(1, openRatio * 0.30 + crossScore * 0.35 + rangeScore * 0.35);
}

// ============================================================
// Temporal classifier
// ============================================================

const TEMPORAL_SCORERS = {
  thanks: scoreForwardFromChin,
  please: scoreCircularChest,
  i_want_to_eat: scorePinchToMouth,
  goodbye: scoreLateralWave,
};

/**
 * Run all allowed temporal scorers against the landmark buffer.
 * @param {Array<{lm: any[], ts: number}>} buffer
 * @param {Set<string>|null} allowedIds — only score these ids; null = all
 * @returns {{id, confidence}|null}
 */
export function classifyTemporal(buffer, allowedIds = null) {
  if (!Array.isArray(buffer) || buffer.length < MIN_BUFFER_FRAMES) return null;
  let bestId = null;
  let bestScore = 0;
  for (const [id, fn] of Object.entries(TEMPORAL_SCORERS)) {
    if (allowedIds && !allowedIds.has(id)) continue;
    const s = fn(buffer);
    if (s > bestScore) {
      bestScore = s;
      bestId = id;
    }
  }
  if (bestScore < TEMPORAL_CONFIDENCE) return null;
  return { id: bestId, confidence: Math.round(bestScore * 100) / 100 };
}

export const MOTION_THRESHOLDS = {
  TEMPORAL_CONFIDENCE,
  MIN_BUFFER_FRAMES,
  MOTION_SUPPRESS_THRESHOLD,
  MOTION_DETECT_THRESHOLD,
  THANKS_DOWNWARD_DELTA,
  THANKS_START_Y_MAX,
  PLEASE_MIN_SWEEP_RADIANS,
  PLEASE_MIN_RADIUS,
  PLEASE_CHEST_Y_MIN,
  PLEASE_CHEST_Y_MAX,
  EAT_PINCH_RATIO,
  EAT_UPWARD_DELTA,
  GOODBYE_MIN_CROSSINGS,
  GOODBYE_MIN_AMPLITUDE,
};
